import { NgIf } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    forwardRef
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CoverageInputDataInputInterface } from '@app/my-zone/shared/insurance-coverage/insurance-coverage-control';
import { AdditionalCoverageInfoModalComponent } from '@app/shared/modals';
import { TooltipModule } from '@app/shared/tooltip/tooltip.module';
import { isArrayEqual } from '@app/shared/utilities/array-utilities';
import {
    WucCardModule,
    WucInputCheckboxModule,
    WucLinkModule, WucPriceModule,
    WucTextHighlightModule,
} from '@inshared/website-ui-components';
import { CoverageIdEnum, NewModal } from 'outshared-lib';
import { CoverageGroupDefault, CoverageInput } from '../../classes';
import { CoverageGroupLayoutEnum } from '../../enums';

@Component({
    selector: 'ins-coverage-dynamic',
    templateUrl: 'coverage-dynamic.component.html',
    styleUrls: ['./coverage-dynamic.component.scss'],
    standalone: true,
    imports: [
        WucInputCheckboxModule,
        WucCardModule,
        WucTextHighlightModule,
        WucLinkModule,
        WucPriceModule,
        TooltipModule,
        FormsModule,
        NgIf,
    ],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => CoverageDynamicComponent),
        multi: true,
    }],
    changeDetection: ChangeDetectionStrategy.OnPush,
})

export class CoverageDynamicComponent implements ControlValueAccessor, OnInit, OnChanges {

    @Input() public group!: CoverageGroupDefault;
    @Input() public selectedIds: string[];

    public input: CoverageInput;
    public coverageGroupLayoutEnum: typeof CoverageGroupLayoutEnum;
    public propagateChange;
    public currentInputIndex: number;
    public values: { enabled: boolean }[];
    public description: string;
    public coverageIdEnum = CoverageIdEnum;

    constructor(
        private newModal: NewModal,
    ) {
        this.coverageGroupLayoutEnum = CoverageGroupLayoutEnum;
    }

    public get value(): { enabled: boolean } {
        return {
            enabled: Boolean(this.values.find(({ enabled }) => enabled)),
        };
    };

    public ngOnInit(): void {
        this.refreshInput();
        this.values = this.createCheckboxes(this.group.inputs);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedIds']) {
            this.refreshInput();

            if (this.propagateChange) {
                return this.propagateChange();
            }
        }
    }

    public writeValue(value: CoverageIdEnum[]): void {
        if (value && value.length > 0 && isArrayEqual(this.input?.coverageIds, value)) {
            this.values[this.currentInputIndex].enabled = true;
        }
    }

    public registerOnChange(fn: any): void {
        this.propagateChange = (): void => {
            fn(this.values[this.currentInputIndex].enabled ? this.input.coverageIds : []);
        };
    }

    public registerOnTouched(): void {
        return;
    }

    public async infoModal(input: CoverageInput): Promise<void> {
        const { AdditionalCoverageInfoModalComponent } = await import('@app/shared/modals');
        this.newModal
            .open<AdditionalCoverageInfoModalComponent>(AdditionalCoverageInfoModalComponent, {
                data: {
                    coverage_id: input.id,
                    coverage_title: input?.data?.title,
                },
            });
    }

    public async displayInformation(data: CoverageInputDataInputInterface): Promise<void> {
        const { CoverageControlInformationModal } = await import('../coverage-control-information/coverage-control-information.modal');
        this.newModal.open(CoverageControlInformationModal, { data });
    }

    private refreshInput(): void {
        this.currentInputIndex = this.group.inputs.findIndex(
            (input) => (Array.isArray(input?.dependsOn) ?
                input.dependsOn.every((dependency) => this.selectedIds?.includes(dependency)) :
                this.selectedIds?.includes(input?.dependsOn)) ?? 0);
        this.input = this.group.inputs[this.currentInputIndex];
    }

    private createCheckboxes(inputs: CoverageInput[]): { enabled: boolean }[] {
        const checkboxes: { enabled: boolean }[] = [];
        for (const input of inputs) {
            checkboxes.push({ enabled: false });
        }
        return checkboxes;
    }
}
